import {cn} from '@tempmee/core'
import {OutlineDotsHorizontalIcon} from '@tempmee/react-icons'
import {Children, Fragment, type HTMLAttributes, type ReactNode, forwardRef, useState} from 'react'

export interface BreadcrumbsProps extends HTMLAttributes<HTMLElement> {
  /**
   * The breadcrumb trail — one node per crumb, in order. Pass links (`<a>`) for
   * ancestors and a plain element for the current page (e.g.
   * `<span aria-current="page">`). Mirrors MUI's `Breadcrumbs` children API.
   */
  children: ReactNode
  /**
   * Node inserted between crumbs. Pass an icon (e.g. `<OutlineChevronRightIcon />`)
   * for the chevron style. @default '/'
   */
  separator?: ReactNode
  /** Collapse the middle into a `…` button past this many crumbs. @default 8 */
  maxItems?: number
  /** Leading crumbs kept visible when collapsed. @default 1 */
  itemsBeforeCollapse?: number
  /** Trailing crumbs kept visible when collapsed. @default 1 */
  itemsAfterCollapse?: number
}

/**
 * Navigation trail showing a page's position in the site hierarchy — the MUI
 * `Breadcrumbs` contract. Pass each crumb as a child (links for ancestors, a
 * plain `aria-current="page"` element for the current page) plus a `separator`.
 * Long trails collapse the middle into a `…` button that expands on click.
 *
 * Anchor children are styled by the component (brand text, hover underline,
 * focus ring), so plain `<a>` works without a separate Link component.
 */
export const Breadcrumbs = forwardRef<HTMLElement, BreadcrumbsProps>(
  (
    {
      className,
      children,
      separator = '/',
      maxItems = 8,
      itemsBeforeCollapse = 1,
      itemsAfterCollapse = 1,
      ...props
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(false)
    const crumbs = Children.toArray(children)
    const collapsed = !expanded && crumbs.length > maxItems

    const visible: ReactNode[] = collapsed
      ? [
          ...crumbs.slice(0, itemsBeforeCollapse),
          <button
            key="ellipsis"
            type="button"
            aria-label="Show all breadcrumbs"
            onClick={() => setExpanded(true)}
            className="flex h-4 w-8 items-center justify-center rounded-[2px] bg-action-disabled-background text-text-primary transition-colors hover:bg-divider focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&_svg]:size-4"
          >
            <OutlineDotsHorizontalIcon />
          </button>,
          ...crumbs.slice(crumbs.length - itemsAfterCollapse),
        ]
      : crumbs

    return (
      <nav
        ref={ref}
        aria-label="breadcrumb"
        className={cn(
          'text-base leading-tight text-text-primary [&_a]:rounded-sm [&_a]:text-text-primary [&_a:hover]:underline [&_a:focus-visible]:outline-none [&_a:focus-visible]:ring-2 [&_a:focus-visible]:ring-primary',
          className,
        )}
        {...props}
      >
        <ol className="flex flex-wrap items-center">
          {visible.map((crumb, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: crumbs are positional and have no stable id
            <Fragment key={i}>
              {i > 0 ? (
                <li
                  aria-hidden
                  className="flex shrink-0 items-center justify-center px-2 text-text-secondary [&_svg]:size-5"
                >
                  {separator}
                </li>
              ) : null}
              <li className="flex items-center whitespace-nowrap">{crumb}</li>
            </Fragment>
          ))}
        </ol>
      </nav>
    )
  },
)

Breadcrumbs.displayName = 'Breadcrumbs'
